import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import projectService, { Project, ProjectMember } from '../../services/projectService';

interface ProjectState {
  projects: Project[];
  selectedProject: Project | null;
  projectMembers: ProjectMember[];
  loading: boolean;
  error: string | null;
}

const initialState: ProjectState = {
  projects: [],
  selectedProject: null,
  projectMembers: [],
  loading: false,
  error: null,
};

// Async thunks
export const fetchProjects = createAsyncThunk(
  'projects/fetchAll',
  async () => {
    return await projectService.getProjects();
  }
);

export const fetchProject = createAsyncThunk(
  'projects/fetchOne',
  async (id: string) => {
    return await projectService.getProject(id);
  }
);

export const createProject = createAsyncThunk(
  'projects/create',
  async (data: any) => {
    return await projectService.createProject(data);
  }
);

export const updateProject = createAsyncThunk(
  'projects/update',
  async ({ id, data }: { id: string; data: any }) => {
    return await projectService.updateProject(id, data);
  }
);

export const deleteProject = createAsyncThunk(
  'projects/delete',
  async (id: string) => {
    await projectService.deleteProject(id);
    return id;
  }
);

export const fetchProjectMembers = createAsyncThunk(
  'projects/fetchMembers',
  async (projectId: string) => {
    return await projectService.getProjectMembers(projectId);
  }
);

const projectSlice = createSlice({
  name: 'projects',
  initialState,
  reducers: {
    clearSelectedProject: (state) => {
      state.selectedProject = null;
      state.projectMembers = [];
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch all projects
      .addCase(fetchProjects.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchProjects.fulfilled, (state, action: PayloadAction<Project[]>) => {
        state.loading = false;
        state.projects = action.payload;
      })
      .addCase(fetchProjects.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch projects';
      })
      // Fetch single project
      .addCase(fetchProject.fulfilled, (state, action: PayloadAction<Project>) => {
        state.selectedProject = action.payload;
      })
      // Create project
      .addCase(createProject.fulfilled, (state, action: PayloadAction<Project>) => {
        state.projects.push(action.payload);
      })
      // Update project
      .addCase(updateProject.fulfilled, (state, action: PayloadAction<Project>) => {
        const index = state.projects.findIndex(p => p.id === action.payload.id);
        if (index !== -1) {
          state.projects[index] = action.payload;
        }
        if (state.selectedProject?.id === action.payload.id) {
          state.selectedProject = action.payload;
        }
      })
      // Delete project
      .addCase(deleteProject.fulfilled, (state, action: PayloadAction<string>) => {
        state.projects = state.projects.filter(p => p.id !== action.payload);
      })
      // Fetch members
      .addCase(fetchProjectMembers.fulfilled, (state, action: PayloadAction<ProjectMember[]>) => {
        state.projectMembers = action.payload;
      });
  },
});

export const { clearSelectedProject, clearError } = projectSlice.actions;
export default projectSlice.reducer;
